import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Modern kit A-frame swing set on a 2x1 tile patch, ~2.2 m high: two angled
// A-frame leg pairs, a top beam along X and two swings (chains + seat slab).
// Each swing's parts share one name so the looping "sway" animation rotates
// the whole assembly about the top beam.
const GALV = "#9aa2a6";
const BLACK = "#2e2e30";
const RED = "#b05a4a";

const BEAM_Y = 2.2;
const LEG_LEN = 2.28;
const LEG_LEAN = 0.21; // radians the A-frame legs splay in Z
const CHAIN_TOP = BEAM_Y - 0.04;
const SEAT_Y = 0.55;
const CHAIN_LEN = CHAIN_TOP - SEAT_Y;

/** One swing (two chain bars + a seat slab), all sharing `name`. */
function swing(x: number, name: string): Part[] {
  const chain = (dx: number): Part => ({
    geometry: new THREE.BoxGeometry(0.025, CHAIN_LEN, 0.025),
    color: BLACK,
    position: [x + dx, SEAT_Y + CHAIN_LEN / 2, 0],
    name,
  });
  return [
    chain(-0.16),
    chain(0.16),
    {
      geometry: new THREE.BoxGeometry(0.42, 0.05, 0.2),
      color: RED,
      position: [x, SEAT_Y, 0],
      name,
    },
  ];
}

/** One A-frame leg pair at `x` (two legs splayed in Z, meeting at the beam). */
function aFrame(x: number): Part[] {
  return [-1, 1].map((s): Part => ({
    geometry: new THREE.BoxGeometry(0.08, LEG_LEN, 0.06),
    color: GALV,
    position: [x, BEAM_Y / 2, (s * Math.sin(LEG_LEAN) * LEG_LEN) / 2],
    rotation: [s * LEG_LEAN, 0, 0],
  }));
}

export const playgroundSwing = defineModel({
  id: "playground-swing",
  batchId: "modern",
  name: "Playground swing",
  tags: ["modern", "outdoor", "playground", "kids", "garden", "swing", "animated"],
  summary: "An A-frame swing set with two gently swaying swings.",
  description:
    "An A-frame playground swing set of the Modern Living kit on a 2x1 tile " +
    "patch, about 2.2 m high: two angled leg pairs, a galvanized top beam " +
    "along X and two chain-hung seats. The looping sway animation rocks the " +
    "two swings a few degrees out of phase about the beam.",
  tiles: { w: 2, h: 1 },
  footprint: 1.3,
  parts(): Part[] {
    return [
      ...aFrame(-0.85),
      ...aFrame(0.85),
      // Top beam along X.
      {
        geometry: new THREE.BoxGeometry(1.9, 0.08, 0.08),
        color: GALV,
        position: [0, BEAM_Y, 0],
      },
      ...swing(-0.4, "swing-left"),
      ...swing(0.4, "swing-right"),
    ];
  },
  animations: [
    {
      id: "sway",
      label: "Sway",
      duration: 2.5,
      loop: true,
      tracks: [
        {
          target: "swing-left",
          pivot: [0, CHAIN_TOP, 0],
          rotation: [
            { t: 0, value: [0.09, 0, 0] },
            { t: 1.25, value: [-0.09, 0, 0], ease: "sine" },
            { t: 2.5, value: [0.09, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "swing-right",
          pivot: [0, CHAIN_TOP, 0],
          rotation: [
            { t: 0, value: [-0.07, 0, 0] },
            { t: 1.25, value: [0.07, 0, 0], ease: "sine" },
            { t: 2.5, value: [-0.07, 0, 0], ease: "sine" },
          ],
        },
      ],
    },
  ],
});
